import React, { useLayoutEffect, useRef, useState } from "react";
import { useIntl } from "react-intl";
import { HeapPHIDecorator } from "@app/components/analytics";
import STATES from "@etc/states.json";
import { messages } from "../messages";
import { Option, Select } from "@natera/form";
import { GetValidationError } from "@natera/platform/lib/hooks/useError";
import { FormField } from "@app/components";

const StateField: React.FC<{
  getValidationError: GetValidationError;
  defaultValue?: string;
}> = ({ getValidationError, defaultValue }) => {
  const intl = useIntl();
  const selectRef = useRef<HTMLDivElement>(null);
  const [selectedState, setSelectedState] = useState<string | undefined>(
    defaultValue
  );

  const handleValueChange = (value: string) => {
    setSelectedState(value);
  };

  useLayoutEffect(() => {
    const root = selectRef.current;
    if (!root) return;

    const anchor = root.querySelector<HTMLElement>(".mdc-select__anchor");
    if (!anchor) return;

    anchor.setAttribute("aria-haspopup", "listbox");

    const updateAriaActivedescendant = () => {
      if (selectedState) {
        const optionId = `state-option-${selectedState}`;
        const option = document.getElementById(optionId);
        if (option) {
          anchor.setAttribute("aria-activedescendant", optionId);
        } else {
          anchor.removeAttribute("aria-activedescendant");
        }
      } else {
        anchor.removeAttribute("aria-activedescendant");
      }
    };

    const observer = new MutationObserver(() => {
      const listboxes = document.querySelectorAll<HTMLElement>(
        ".mdc-select__menu .mdc-deprecated-list"
      );
      listboxes.forEach((listbox) => {
        if (!listbox.hasAttribute("role")) {
          listbox.setAttribute("role", "listbox");
          listbox.setAttribute("aria-labelledby", "state-select-label");
        }
      });
      updateAriaActivedescendant();
    });

    updateAriaActivedescendant();

    observer.observe(document.body, { childList: true, subtree: true });

    return () => observer.disconnect();
  }, [selectedState]);

  return (
    <FormField
      title={intl.formatMessage(messages.addressFormState)}
      label={intl.formatMessage(messages.addressFormState)}
      error={getValidationError("state")}
      required
      labelId="state-select-label"
      withPadding
    >
      <HeapPHIDecorator protectAttr={["value"]}>
        <div ref={selectRef}>
          <Select
            labelId="state-select-label"
            id="state-select"
            outlined
            required
            name="state"
            data-testid="state"
            role="combobox"
            placeholder={intl.formatMessage(
              messages.addressFormStatePlaceholder
            )}
            value={selectedState}
            onValueChange={handleValueChange}
            floating={true}
            aria-describedby={
              getValidationError("state") ? "state-error" : undefined
            }
          >
            {STATES.map((item) => {
              const optionId = `state-option-${item.abbreviation}`;
              return (
                <Option
                  key={item.abbreviation}
                  id={optionId}
                  value={item.abbreviation}
                  aria-selected={selectedState === item.abbreviation}
                >
                  {item.name}
                </Option>
              );
            })}
          </Select>
        </div>
      </HeapPHIDecorator>
    </FormField>
  );
};

export { StateField };
